/*! 
 * Parenting Optimize - V3 Reasoning Engine
 * File: parenting-engine.js
 * Version: 3.0.0
 *
 * Purpose:
 * Converts scenario + observations + adaptive answers + child memory into:
 * - construct scores
 * - confidence bands
 * - adaptive questions
 * - working understanding
 * - practical personalized plan
 * - child-profile learning updates
 *
 * This file should not render UI.
 * This file should not contain the large content libraries.
 * Full knowledge/content belongs in parenting-knowledge.js.
 */

(function (window) {
  "use strict";

  const PO = (window.PO = window.PO || {});

  // ==========================================================================
  // 01. ENGINE NAMESPACE
  // ==========================================================================

  PO.Engine = {
    version: "3.0.0",

    // ------------------------------------------------------------------------
    // Public API
    // ------------------------------------------------------------------------

    /**
     * Main one-call API used by parenting-app.js.
     * Accepts the current session context and returns a full reasoning result.
     */
    generatePlan(context = {}) {
      const ctx = this.normalizeContext(context);
      const reasoning = this.reason(ctx);
      const plan = this.buildPlan(ctx, reasoning);

      return {
        id: this.uid("plan"),
        version: this.version,
        createdAt: this.now(),
        scenario: reasoning.scenario,
        situation: reasoning.situationKey,
        situationLabel: reasoning.scenario?.label || this.labelSituation(reasoning.situationKey),
        title: plan.title,
        subtitle: plan.subtitle,
        estimatedTime: plan.estimatedTime,
        workingUnderstanding: reasoning.workingUnderstanding,
        constructs: reasoning.topConstructs,
        constructScores: reasoning.constructScores,
        confidence: reasoning.overallConfidence,
        adaptiveQuestions: reasoning.adaptiveQuestions,
        steps: plan.steps,
        scripts: plan.scripts,
        why: plan.why,
        avoid: plan.avoid,
        whatToNotice: plan.whatToNotice,
        contribution: plan.contribution,
        learningNote: plan.learningNote,
        referralFlags: reasoning.referralFlags,
        nextLearningTargets: reasoning.nextLearningTargets,
        memoryPatch: this.buildChildProfilePatch(ctx, reasoning, plan),
        debug: {
          observations: ctx.observations,
          adaptiveAnswers: ctx.adaptiveAnswers,
          scoreInputs: reasoning.scoreInputs
        }
      };
    },

    /**
     * Returns only the reasoning layer. Useful after observation selection,
     * before showing the final plan.
     */
    reason(context = {}) {
      const ctx = this.normalizeContext(context);
      const scenario = this.resolveScenario(ctx);
      const situationKey = scenario?.situationKey || ctx.situation || "general";
      const observations = this.normalizeObservations(ctx.observations);
      const scoreInputs = this.mapObservationsToConstructs(observations);
      const constructScores = this.calculateConstructScores(ctx, scoreInputs, scenario);
      const topConstructs = this.rankConstructs(constructScores).slice(0, 5);
      const adaptiveQuestions = this.chooseAdaptiveQuestions(ctx, topConstructs);
      const workingUnderstanding = this.generateWorkingUnderstanding(ctx, scenario, topConstructs);
      const overallConfidence = this.calculateOverallConfidence(ctx, observations, topConstructs);
      const referralFlags = this.detectReferralFlags(ctx, topConstructs);
      const nextLearningTargets = this.identifyNextLearningTargets(ctx, topConstructs, adaptiveQuestions);

      return {
        scenario,
        situationKey,
        observations,
        scoreInputs,
        constructScores,
        topConstructs,
        adaptiveQuestions,
        workingUnderstanding,
        overallConfidence,
        referralFlags,
        nextLearningTargets
      };
    },

    /**
     * Used by the app immediately after observations are selected.
     */
    getAdaptiveQuestions(context = {}, limit = 5) {
      const ctx = this.normalizeContext(context);
      const reasoning = this.reason(ctx);
      return reasoning.adaptiveQuestions.slice(0, limit);
    },

    /**
     * Used after parent feedback to update child memory.
     */
    learnFromFeedback(context = {}, feedback = {}) {
      const ctx = this.normalizeContext(context);
      const result = feedback.result || feedback.outcome || "unknown";
      const rule = this.knowledge().getFeedbackRule
        ? this.knowledge().getFeedbackRule(result)
        : null;

      const patch = {
        lastUpdated: this.now(),
        feedbackHistoryAppend: {
          id: this.uid("feedback"),
          result,
          notes: feedback.notes || "",
          planId: feedback.planId || ctx.activePlan?.id || null,
          createdAt: this.now()
        }
      };

      if (["worked", "somewhat"].includes(result)) {
        patch.successfulStrategiesAppend = feedback.strategy || ctx.activePlan?.insightKey || "current-plan";
      }

      if (["did-not-work", "worse"].includes(result)) {
        patch.unsuccessfulStrategiesAppend = feedback.strategy || ctx.activePlan?.insightKey || "current-plan";
      }

      if (rule) {
        patch.learningMessage = rule.parentMessage;
        patch.futureBias = rule.futureBias || [];
      }

      return patch;
    },

    // ==========================================================================
    // 02. CONTEXT NORMALIZATION
    // ==========================================================================

    normalizeContext(context = {}) {
      const answers = context.answers || {};
      const child = context.child || {};
      const family = context.family || {};
      const parent = context.parent || {};

      const observations = this.firstArray(
        context.observations,
        answers.observations,
        answers.selectedObservations,
        context.selectedObservations,
        []
      );

      const adaptiveAnswers = this.normalizeAdaptiveAnswers(
        context.adaptiveAnswers || answers.adaptiveAnswers || answers.clarifyingAnswers || {}
      );

      return {
        child,
        parent,
        family,
        scenarioId: context.scenarioId || answers.scenarioId || answers.scenario || null,
        scenarioKey: context.scenarioKey || answers.scenarioKey || null,
        situation: context.situation || answers.situation || child.mainChallenge || "general",
        observations,
        adaptiveAnswers,
        parentNotes: context.parentNotes || answers.parentNotes || "",
        history: Array.isArray(context.history) ? context.history : [],
        feedback: Array.isArray(context.feedback) ? context.feedback : [],
        childMemory: context.childMemory || child.v3Memory || child.memory || {},
        activePlan: context.activePlan || null,
        timestamp: this.now(),
        raw: context
      };
    },

    normalizeAdaptiveAnswers(input = {}) {
      if (Array.isArray(input)) {
        return input.reduce((acc, item) => {
          if (item && item.id) acc[item.id] = item.value ?? item.answer ?? item;
          return acc;
        }, {});
      }
      return input && typeof input === "object" ? input : {};
    },

    normalizeObservations(observations = []) {
      return this.firstArray(observations, []).map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          return String(item.label || item.value || item.name || item.id || "").trim();
        }
        return "";
      }).filter(Boolean);
    },

    firstArray(...values) {
      for (const value of values) {
        if (Array.isArray(value)) return value;
      }
      return [];
    },

    knowledge() {
      if (!PO.Knowledge) {
        throw new Error("Parenting Optimize: PO.Knowledge is required before PO.Engine.");
      }
      return PO.Knowledge;
    },

    // ==========================================================================
    // 03. SCENARIO RESOLUTION
    // ==========================================================================

    resolveScenario(ctx) {
      const K = this.knowledge();

      if (K.getV3Scenario) {
        const fromId = K.getV3Scenario(ctx.scenarioId);
        if (fromId) return fromId;

        const fromKey = K.getV3Scenario(ctx.scenarioKey);
        if (fromKey) return fromKey;
      }

      const scenarios = K.v3?.scenarios || {};
      const normalizedSituation = this.normalizeKey(ctx.situation);

      const match = Object.keys(scenarios).find((id) => {
        const item = scenarios[id];
        return [item.key, item.situationKey, item.label].some((value) => {
          return this.normalizeKey(value) === normalizedSituation;
        });
      });

      return match ? { id: match, ...scenarios[match] } : null;
    },

    // ==========================================================================
    // 04. OBSERVATION → CONSTRUCT SCORING
    // ==========================================================================

    mapObservationsToConstructs(observations = []) {
      const K = this.knowledge();
      const result = [];

      observations.forEach((label) => {
        const constructIds = K.getV3ConstructsForObservation
          ? K.getV3ConstructsForObservation(label)
          : this.fallbackConstructsForObservation(label);

        constructIds.forEach((constructId, index) => {
          result.push({
            observation: label,
            constructId,
            rank: index + 1,
            weight: this.rankWeight(index)
          });
        });
      });

      return result;
    },

    calculateConstructScores(ctx, scoreInputs = [], scenario = null) {
      const K = this.knowledge();
      const scores = {};

      this.listConstructIds().forEach((id) => {
        scores[id] = {
          id,
          score: 0,
          evidence: [],
          adaptiveEvidence: [],
          memoryEvidence: [],
          confidence: "low"
        };
      });

      scoreInputs.forEach((input) => {
        if (!scores[input.constructId]) return;
        scores[input.constructId].score += input.weight;
        scores[input.constructId].evidence.push(input.observation);
      });

      this.applyScenarioBias(scores, scenario);
      this.applyAdaptiveAnswerScores(scores, ctx.adaptiveAnswers);
      this.applyChildMemoryScores(scores, ctx.childMemory);
      this.applyParentFamilyContext(scores, ctx);

      Object.keys(scores).forEach((id) => {
        const construct = K.getV3Construct ? K.getV3Construct(id) : null;
        const score = scores[id].score;
        scores[id].label = construct?.label || construct?.name || id;
        scores[id].meaning = construct?.parentMeaning || "";
        scores[id].confidence = K.getV3ConfidenceBand
          ? K.getV3ConfidenceBand(score)
          : this.confidenceBand(score);
        scores[id].evidence = this.unique(scores[id].evidence);
        scores[id].adaptiveEvidence = this.unique(scores[id].adaptiveEvidence);
        scores[id].memoryEvidence = this.unique(scores[id].memoryEvidence);
      });

      return scores;
    },

    rankWeight(index) {
      // Ordered mapping is meaningful: first construct is strongest.
      const weights = [3, 2, 1.25, 0.75, 0.5];
      return weights[index] || 0.4;
    },

    applyScenarioBias(scores, scenario) {
      if (!scenario) return;

      const key = scenario.key || scenario.situationKey || "";
      const label = scenario.label || "scenario context";

      const biasMap = {
        "bedtime-struggles": ["C02", "C07", "C12", "C13"],
        "morning-routine": ["C02", "C03", "C04"],
        "homework-refusal": ["C08", "C09", "C03", "C07"],
        "doesnt-listen": ["C03", "C04", "C01", "C15"],
        "negotiates-everything": ["C01", "C10", "C11", "C14"],
        "tantrums-meltdowns": ["C06", "C05", "C12"],
        "emotional-outbursts": ["C06", "C05", "C07"],
        "screen-time-battles": ["C02", "C11", "C01", "C14"],
        "sibling-conflict": ["C10", "C13", "C01"],
        "lying": ["C07", "C08", "C14"],
        "anxiety-worry": ["C07", "C13", "C08"],
        "arguing": ["C01", "C10", "C11", "C06"]
      };

      const ids = biasMap[key] || [];
      ids.forEach((id, index) => {
        if (!scores[id]) return;
        scores[id].score += index === 0 ? 1.1 : 0.65;
        scores[id].evidence.push(label);
      });
    },

    applyAdaptiveAnswerScores(scores, adaptiveAnswers = {}) {
      const K = this.knowledge();
      const questions = K.v3?.adaptiveQuestions || [];

      questions.forEach((question) => {
        const raw = adaptiveAnswers[question.id];
        if (raw === undefined || raw === null || raw === "") return;

        const answer = this.normalizeKey(raw.value ?? raw.answer ?? raw);
        const measures = question.measures || [];

        measures.forEach((id) => {
          if (!scores[id]) return;
          const weight = this.adaptiveAnswerWeight(question.id, answer, id);
          if (!weight) return;
          scores[id].score += weight;
          scores[id].adaptiveEvidence.push(question.question);
        });
      });
    },

    adaptiveAnswerWeight(questionId, answer, constructId) {
      // The UI can pass simple values. This keeps the engine tolerant.
      if (["no", "false", "none", "not-really"].includes(answer)) return 0;
      if (["yes", "true", "often", "always", "usually"].includes(answer)) return 1.25;

      const targeted = {
        AQ01: { change: "C02", anxious: "C07", worry: "C07", upset: "C02" },
        AQ02: { persuade: "C01", negotiate: "C14", upset: "C06", fairness: "C10", persistent: "C11" },
        AQ03: { easier: "C01", yes: "C01" },
        AQ04: { oneparent: "C15", one_parent: "C15", no: "C15", both: null },
        AQ05: { closeness: "C13", space: "C06", either: "C06" },
        AQ06: { meltdown: "C06", unfair: "C10", angry: "C06" },
        AQ07: { hard: "C08", difficult: "C08", told: "C01", control: "C01" },
        AQ08: { forget: "C03", refuses: "C01", refuse: "C01" },
        AQ09: { tired: "C06", afterschool: "C06", after_school: "C06" },
        AQ10: { sounds: "C12", clothes: "C12", food: "C12", sensory: "C12", yes: "C12" },
        AQ11: { keepsasking: "C07", keeps_asking: "C07", repeated: "C07" },
        AQ12: { yes: "C14", works: "C14", usually: "C14" }
      };

      const map = targeted[questionId] || {};
      const target = map[answer];
      if (target === null) return 0;
      if (target === constructId) return 1.75;

      // If parent gave an unstructured text answer, still add light evidence.
      return answer.length > 3 ? 0.45 : 0;
    },

    applyChildMemoryScores(scores, childMemory = {}) {
      const likely = childMemory.likelyConstructs || childMemory.constructs || [];
      const confidence = childMemory.constructConfidence || childMemory.confidence || {};

      if (Array.isArray(likely)) {
        likely.forEach((item) => {
          const id = typeof item === "string" ? item : item.id || item.constructId;
          if (!id || !scores[id]) return;
          const memoryWeight = item.score ? Math.min(Number(item.score) / 3, 2) : 0.85;
          scores[id].score += memoryWeight;
          scores[id].memoryEvidence.push("Previous child profile learning");
        });
      }

      Object.keys(confidence || {}).forEach((id) => {
        if (!scores[id]) return;
        const band = String(confidence[id]).toLowerCase();
        const add = band === "high" ? 1.4 : band === "medium" ? 0.8 : 0.3;
        scores[id].score += add;
        scores[id].memoryEvidence.push("Stored confidence from prior sessions");
      });
    },

    applyParentFamilyContext(scores, ctx) {
      const parentResponse = ctx.parent.responseStyle || ctx.raw?.answers?.parentResponse;
      const familyStructure = ctx.family.structure || ctx.raw?.answers?.familyStructure;

      if (parentResponse === "repeat") this.addContextScore(scores, "C14", 0.7, "Parent may be stuck in a repeat-resist loop");
      if (parentResponse === "explain") this.addContextScore(scores, "C01", 0.45, "Explanations may invite negotiation");
      if (parentResponse === "give-in") this.addContextScore(scores, "C14", 0.8, "Child may learn persistence changes outcomes");
      if (parentResponse === "variable") this.addContextScore(scores, "C15", 1.1, "Boundaries may feel inconsistent");

      if (["shared", "other"].includes(familyStructure)) {
        this.addContextScore(scores, "C15", 1, "Family setup may create mixed messages");
      }
    },

    addContextScore(scores, id, amount, note) {
      if (!scores[id]) return;
      scores[id].score += amount;
      scores[id].memoryEvidence.push(note);
    },

    rankConstructs(constructScores = {}) {
      return Object.keys(constructScores)
        .map((id) => constructScores[id])
        .filter((item) => item.score > 0)
        .sort((a, b) => b.score - a.score)
        .map((item, index) => ({
          ...item,
          rank: index + 1
        }));
    },

    listConstructIds() {
      const K = this.knowledge();
      const constructs = K.v3?.constructs || {};
      const fromKnowledge = Object.keys(constructs);
      if (fromKnowledge.length) return fromKnowledge;
      return ["C01", "C02", "C03", "C04", "C05", "C06", "C07", "C08", "C09", "C10", "C11", "C12", "C13", "C14", "C15"];
    },

    // ==========================================================================
    // 05. ADAPTIVE QUESTIONS
    // ==========================================================================

    chooseAdaptiveQuestions(ctx, topConstructs = [], limit = 5) {
      const K = this.knowledge();
      const answeredIds = new Set(Object.keys(ctx.adaptiveAnswers || {}));
      const topIds = topConstructs.slice(0, 5).map((item) => item.id);
      const questions = K.v3?.adaptiveQuestions || [];

      return questions
        .map((question) => {
          const overlap = (question.measures || []).filter((id) => topIds.includes(id)).length;
          const uncertaintyBoost = (question.measures || []).some((id) => {
            const construct = topConstructs.find((item) => item.id === id);
            return construct && construct.confidence !== "high";
          }) ? 0.5 : 0;

          return {
            ...question,
            priority: overlap + uncertaintyBoost,
            alreadyAnswered: answeredIds.has(question.id)
          };
        })
        .filter((question) => question.priority > 0 && !question.alreadyAnswered)
        .sort((a, b) => b.priority - a.priority)
        .slice(0, limit)
        .map(({ priority, alreadyAnswered, ...question }) => question);
    },

    // ==========================================================================
    // 06. WORKING UNDERSTANDING
    // ==========================================================================

    generateWorkingUnderstanding(ctx, scenario, topConstructs = []) {
      const childName = ctx.child?.name || ctx.child?.childName || "your child";
      const top = topConstructs[0];
      const second = topConstructs[1];

      if (!top) {
        return {
          headline: "We need a little more information.",
          summary: `Right now, there is not enough pattern data to understand what is driving this for ${childName}.`,
          bullets: [
            "Choose the behaviors you are actually seeing.",
            "Answer a few follow-up questions.",
            "The plan will become more specific as the app learns."
          ]
        };
      }

      const summaryParts = [
        `Our current working understanding is that ${top.label.toLowerCase()} may be the strongest driver.`
      ];

      if (second) {
        summaryParts.push(`${second.label.toLowerCase()} may also be contributing.`);
      }

      summaryParts.push("This is not a diagnosis. It is a practical pattern to test with one small plan.");

      return {
        headline: "Our current working understanding",
        summary: summaryParts.join(" "),
        bullets: topConstructs.slice(0, 3).map((construct) => {
          const evidence = construct.evidence && construct.evidence.length
            ? ` Seen in: ${construct.evidence.slice(0, 2).join(", ")}.`
            : "";
          return `${construct.label} — ${construct.confidence} confidence.${evidence}`;
        })
      };
    },

    calculateOverallConfidence(ctx, observations, topConstructs) {
      if (!topConstructs.length) return "low";
      const answeredCount = Object.keys(ctx.adaptiveAnswers || {}).length;
      const topScore = topConstructs[0].score;
      const evidenceCount = observations.length + answeredCount + (ctx.feedback || []).length;

      if (topScore >= 6 && evidenceCount >= 5) return "high";
      if (topScore >= 3.5 && evidenceCount >= 3) return "medium";
      return "low";
    },

    identifyNextLearningTargets(ctx, topConstructs = [], adaptiveQuestions = []) {
      const targets = [];

      topConstructs.slice(0, 3).forEach((construct) => {
        if (construct.confidence !== "high") {
          targets.push(`Learn whether ${construct.label.toLowerCase()} is truly driving the pattern.`);
        }
      });

      adaptiveQuestions.slice(0, 2).forEach((question) => {
        targets.push(question.question);
      });

      if (!targets.length) {
        targets.push("Track whether tonight's plan reduces conflict or only delays it.");
      }

      return this.unique(targets).slice(0, 4);
    },

    detectReferralFlags(ctx, topConstructs = []) {
      const observations = ctx.observations.map((x) => this.normalizeKey(x));
      const flags = [];

      const aggression = ["hits", "kicks", "throwsobjects", "runsaway"].some((key) => observations.includes(key));
      if (aggression) {
        flags.push({
          level: "watch",
          message: "Physical aggression or running away may require extra adult support and safety planning."
        });
      }

      const panic = observations.includes("panicbeforeevents") || observations.includes("stomachachesbeforeschool");
      if (panic) {
        flags.push({
          level: "watch",
          message: "Repeated panic, school avoidance, or physical anxiety symptoms may be worth discussing with a qualified professional."
        });
      }

      const sensory = topConstructs.find((item) => item.id === "C12" && item.confidence === "high");
      if (sensory) {
        flags.push({
          level: "consider",
          message: "Strong sensory patterns may deserve a more careful developmental or occupational-therapy conversation."
        });
      }

      return flags;
    },

    // ==========================================================================
    // 07. PLAN BUILDER
    // ==========================================================================

    buildPlan(ctx, reasoning) {
      const scenarioLabel = reasoning.scenario?.label || this.labelSituation(reasoning.situationKey);
      const top = reasoning.topConstructs[0];
      const secondary = reasoning.topConstructs[1];
      const interventions = reasoning.topConstructs.slice(0, 3).map((construct) => ({
        construct,
        intervention: this.getIntervention(construct.id)
      }));

      const steps = this.buildSteps(ctx, reasoning, interventions);
      const avoid = this.buildAvoidList(reasoning, interventions);
      const scripts = this.buildScripts(ctx, reasoning, interventions);
      const why = this.buildWhy(ctx, reasoning, interventions);
      const whatToNotice = this.buildWhatToNotice(reasoning);
      const contribution = this.buildContributionIdea(ctx, reasoning);
      const learningNote = this.buildLearningNote(ctx, reasoning);

      return {
        title: top ? `${scenarioLabel}: ${top.label}` : `${scenarioLabel}: Start with the pattern`,
        subtitle: secondary
          ? `A short plan built around ${top.label.toLowerCase()} with ${secondary.label.toLowerCase()} in mind.`
          : "A short plan based on what you selected so far.",
        estimatedTime: this.requiresCalmingFirst(reasoning) ? "5 minutes" : "3 minutes",
        steps,
        scripts,
        why,
        avoid,
        whatToNotice,
        contribution,
        learningNote
      };
    },

    buildSteps(ctx, reasoning, interventions) {
      const topId = reasoning.topConstructs[0]?.id;
      const helps = this.flatten(interventions.map((item) => item.intervention?.usuallyHelps || []));
      const childName = ctx.child?.name || "your child";

      const stepLibrary = {
        C01: [
          "Offer two acceptable choices so your child has some influence without changing the rule.",
          "Use collaboration language: “Let’s figure out the first step.”",
          "Give one small responsibility connected to the situation."
        ],
        C02: [
          "Give a short warning before the transition.",
          "Use a countdown or visual routine instead of a sudden demand.",
          "Make the first move tiny: stand up, walk to the doorway, or put one item away."
        ],
        C03: [
          "Give one instruction at a time.",
          "Use a visual cue, checklist, or physical prompt.",
          "Check completion before adding the next step."
        ],
        C04: [
          "Get close before giving the instruction.",
          "Reduce distractions and ask for eyes or a quick touch cue.",
          "Avoid calling instructions from across the room."
        ],
        C05: [
          "Start with validation before correction.",
          "Use a softer tone than the moment seems to invite.",
          "Correct privately when possible."
        ],
        C06: [
          "Regulate first: fewer words, lower voice, safer space.",
          "Do not teach during the peak of the emotion.",
          "When calm returns, practice one better next step."
        ],
        C07: [
          "Make the next step predictable.",
          "Give reassurance once, then shift to a small action.",
          "Use gradual exposure instead of forcing the whole situation at once."
        ],
        C08: [
          "Reduce the task size until it feels startable.",
          "Praise effort before outcome.",
          "Start together briefly, then step back."
        ],
        C09: [
          "Create one small win quickly.",
          "Use encouragement that names effort.",
          "Avoid public correction or comparison."
        ],
        C10: [
          "Give one short reason for the rule.",
          "Use consistency so fairness does not turn into debate.",
          "Acknowledge the feeling without reopening the decision."
        ],
        C11: [
          "Set the boundary and create a closure phrase.",
          "Stop the debate after one repeat.",
          "Give time to shift without chasing the argument."
        ],
        C12: [
          "Check whether sound, clothing, food, light, or touch is making this harder.",
          "Adjust the environment where reasonable.",
          "Do not force discomfort as the first strategy."
        ],
        C13: [
          "Connect before correction.",
          "Give a short moment of closeness or reassurance before the demand.",
          "After the limit, repair the relationship briefly."
        ],
        C14: [
          "Decide the boundary before the discussion starts.",
          "Do not give in after arguing has begun.",
          "Reward calm cooperation, not persistence after no."
        ],
        C15: [
          "Use the same wording each time.",
          "Adults should align privately before the child hears the answer.",
          "Keep the rule simple enough to enforce consistently."
        ]
      };

      let steps = stepLibrary[topId] || [];

      if (!steps.length && helps.length) {
        steps = helps.slice(0, 3).map((help) => `Use ${String(help).toLowerCase()} as the next small strategy.`);
      }

      if (!steps.length) {
        steps = [
          `Name the next step calmly for ${childName}.`,
          "Make the choice simple and concrete.",
          "Follow through without adding a long lecture."
        ];
      }

      return steps.slice(0, 3);
    },

    buildAvoidList(reasoning, interventions) {
      const avoid = this.flatten(interventions.map((item) => item.intervention?.usuallyBackfires || []));
      const custom = [];

      if (this.requiresCalmingFirst(reasoning)) {
        custom.push("Lectures during the emotional peak");
      }

      reasoning.topConstructs.slice(0, 3).forEach((construct) => {
        if (construct.id === "C01") custom.push("Turning the moment into a power struggle");
        if (construct.id === "C14") custom.push("Changing the rule after repeated arguing");
        if (construct.id === "C03") custom.push("Giving several instructions at once");
        if (construct.id === "C15") custom.push("Mixed messages from adults");
      });

      const combined = this.unique(custom.concat(avoid));
      return combined.length ? combined.slice(0, 3) : [
        "Adding new consequences while angry",
        "Repeating the same instruction many times",
        "Trying to teach before the child is calm"
      ];
    },

    buildScripts(ctx, reasoning) {
      const topId = reasoning.topConstructs[0]?.id;
      const scripts = {
        C01: "You can choose this way or that way. The rule stays the same.",
        C02: "In five minutes we are switching. When the timer rings, the first step is just standing up.",
        C03: "One step: put the folder in your bag. Then I’ll tell you the next step.",
        C04: "I need your eyes for one second. Shoes go on now.",
        C05: "I see this feels big. I’m going to stay calm and help you through the next step.",
        C06: "You are having a hard time. I will keep everyone safe. We will talk after your body calms.",
        C07: "I know you feel unsure. I’ll answer once, and then we’ll take the first small step.",
        C08: "You do not have to finish all of it now. Start with one small part.",
        C09: "This is hard, and you can start small. I’m looking for effort, not perfect.",
        C10: "I hear that it feels unfair. The reason is simple: this is the rule for tonight.",
        C11: "I answered. I’m not debating now. You can choose A or B.",
        C12: "Let’s check what is bothering your body first, then we’ll solve the next step.",
        C13: "I’m with you. First a quick hug, then we do the next step.",
        C14: "The answer is still no. Calm asking can work next time; arguing will not change it now.",
        C15: "This is the rule. The adults will use the same answer."
      };

      return {
        main: scripts[topId] || "I’m going to help you with the next step. We’ll keep it simple.",
        repair: "That was a hard moment. We are okay. Next time we will practice one better step."
      };
    },

    buildWhy(ctx, reasoning, interventions) {
      const top = reasoning.topConstructs[0];
      const childName = ctx.child?.name || "your child";

      if (!top) {
        return "The plan starts simple because we need more information before assuming what is driving the behavior.";
      }

      const intervention = interventions[0]?.intervention;
      const helps = intervention?.usuallyHelps || [];
      const helpText = helps.length ? `Strategies like ${helps.slice(0, 2).join(" and ").toLowerCase()} often help this pattern.` : "The plan uses a small, testable step.";

      return `${childName} may not simply be trying to be difficult. The strongest current pattern is ${top.label.toLowerCase()} (${top.confidence} confidence). ${top.meaning ? top.meaning + ". " : ""}${helpText}`;
    },

    buildWhatToNotice(reasoning) {
      const top = reasoning.topConstructs[0];
      if (!top) {
        return [
          "Which behavior happens most often?",
          "What time of day makes it worse?",
          "What helps the child recover faster?"
        ];
      }

      const notice = {
        C01: ["Does giving choices reduce arguing?", "Does your child cooperate more when responsibility is included?"],
        C02: ["Do warnings reduce the reaction?", "Is the first step harder than the whole task?"],
        C03: ["Does one-step instruction work better?", "Does a visual cue reduce reminders?"],
        C04: ["Does getting close work better than calling out?", "Are distractions part of the pattern?"],
        C05: ["Does tone change the reaction?", "Does privacy help correction land better?"],
        C06: ["How long does recovery take?", "Do fewer words calm faster?"],
        C07: ["Does reassurance settle once or keep repeating?", "Does predictability reduce worry?"],
        C08: ["Does shrinking the task increase starting?", "Is fear of mistakes stronger than laziness?"],
        C09: ["Does encouragement increase effort?", "Does public correction make avoidance worse?"],
        C10: ["Does a short reason help?", "Does fairness become a debate loop?"],
        C11: ["Does the argument stop faster with a closure phrase?", "Does chasing the debate extend it?"],
        C12: ["Are sensory triggers present?", "Does changing the environment reduce conflict?"],
        C13: ["Does connection before correction reduce resistance?", "Does distance make it worse?"],
        C14: ["Does consistent follow-through reduce negotiating over time?", "Does giving in make the next conflict longer?"],
        C15: ["Are adults giving different answers?", "Does same wording reduce testing?"]
      };

      return notice[top.id] || ["What helped?", "What backfired?", "What should we test next?"];
    },

    buildContributionIdea(ctx) {
      const K = this.knowledge();
      const strengths = ctx.child?.strengths || [];
      const first = Array.isArray(strengths) ? strengths[0] : null;
      const profile = K.getChildStrength ? K.getChildStrength(first) : null;

      if (profile && profile.contributionIdeas && profile.contributionIdeas.length) {
        return {
          title: "Contribution idea",
          text: profile.contributionIdeas[0],
          reason: profile.praiseStyle || "Use contribution to help the child feel capable, not only corrected."
        };
      }

      return {
        title: "Contribution idea",
        text: "Give this child one meaningful family job this week so they feel useful, not just corrected.",
        reason: "Useful responsibility often improves cooperation and confidence."
      };
    },

    buildLearningNote(ctx, reasoning) {
      const totalFeedback = (ctx.feedback || []).length;
      if (!totalFeedback) {
        return "This is an early working plan. Feedback will make future recommendations more accurate for this child.";
      }

      const worked = ctx.feedback.filter((f) => f.result === "worked" || f.result === "somewhat").length;
      const failed = ctx.feedback.filter((f) => f.result === "did-not-work" || f.result === "worse").length;

      if (worked > failed) return "Some previous strategies appear to be helping. We will bias future plans toward similar structure.";
      if (failed > worked) return "Some previous strategies did not help. We should simplify and test a different driver.";
      return "Feedback is mixed, so the next plan should stay small and easy to test.";
    },

    requiresCalmingFirst(reasoning) {
      return reasoning.topConstructs.some((item) => ["C05", "C06", "C07", "C12"].includes(item.id) && item.score >= 3);
    },

    getIntervention(constructId) {
      const K = this.knowledge();
      return K.getV3Intervention ? K.getV3Intervention(constructId) : K.v3?.interventions?.[constructId] || null;
    },

    // ==========================================================================
    // 08. CHILD MEMORY PATCH
    // ==========================================================================

    buildChildProfilePatch(ctx, reasoning, plan) {
      const child = ctx.child || {};
      const top = reasoning.topConstructs.slice(0, 5);
      const confidence = {};

      top.forEach((construct) => {
        confidence[construct.id] = construct.confidence;
      });

      return {
        childId: child.id || child.childId || null,
        childName: child.name || child.childName || "",
        age: child.age || "",
        activeScenariosAppend: reasoning.scenario?.id || reasoning.scenario?.key || reasoning.situationKey,
        observationHistoryAppend: reasoning.observations.map((label) => ({
          label,
          scenario: reasoning.scenario?.id || reasoning.situationKey,
          createdAt: this.now()
        })),
        likelyConstructs: top.map((construct) => ({
          id: construct.id,
          label: construct.label,
          score: Number(construct.score.toFixed(2)),
          confidence: construct.confidence,
          evidence: construct.evidence.slice(0, 4)
        })),
        constructConfidence: confidence,
        lastRecommendation: {
          title: plan.title,
          steps: plan.steps,
          createdAt: this.now()
        },
        nextLearningTargets: reasoning.nextLearningTargets,
        referralFlags: reasoning.referralFlags,
        lastUpdated: this.now()
      };
    },

    applyChildProfilePatch(profile = {}, patch = {}) {
      const next = { ...profile };
      const appendList = (key, value) => {
        const current = Array.isArray(next[key]) ? next[key] : [];
        const values = Array.isArray(value) ? value : [value];
        next[key] = current.concat(values).filter(Boolean);
      };

      Object.keys(patch).forEach((key) => {
        if (key.endsWith("Append")) {
          const target = key.replace(/Append$/, "");
          appendList(target, patch[key]);
        } else {
          next[key] = patch[key];
        }
      });

      next.lastUpdated = this.now();
      return next;
    },

    // ==========================================================================
    // 09. BACKWARD COMPATIBILITY WITH V1 APP
    // ==========================================================================

    labelSituation(value) {
      const K = PO.Knowledge;
      if (K?.getSituation) {
        const situation = K.getSituation(value);
        if (situation?.label) return situation.label;
      }

      return String(value || "Parenting Moment")
        .replace(/[-_]/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },

    fallbackConstructsForObservation(label) {
      const map = PO.Knowledge?.v3?.observationConstructMap || {};
      return map[label] || [];
    },

    confidenceBand(score) {
      if (score >= 6) return "high";
      if (score >= 3) return "medium";
      return "low";
    },

    // ==========================================================================
    // 10. UTILITIES
    // ==========================================================================

    normalizeKey(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[’']/g, "")
        .replace(/[^a-z0-9]+/g, "")
        .trim();
    },

    flatten(list) {
      return (list || []).reduce((acc, item) => acc.concat(item || []), []);
    },

    unique(list) {
      return Array.from(new Set((list || []).filter(Boolean)));
    },

    now() {
      return new Date().toISOString();
    },

    uid(prefix = "engine") {
      return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    }
  };

})(window);


/* ==========================================================================
 * Parenting Optimize Engine — V3.1 Runtime Revision Patch
 * This patch upgrades the complete V3 engine without removing the stable V3
 * architecture above. It connects the engine to the revised V3.1 knowledge
 * library, including narrative recognition, adaptive answer scoring, age bands,
 * and scenario-specific guidance.
 * ========================================================================== */
(function (window) {
  "use strict";

  const PO = (window.PO = window.PO || {});
  const E = PO.Engine;

  if (!E) {
    throw new Error("Parenting Optimize V3 engine must load before the V3.1 patch.");
  }

  E.version = "3.1.0";

  // --------------------------------------------------------------------------
  // 1. CONTEXT NORMALIZATION
  // Adds narrative recognition and merges narrative observations with selected
  // observations without losing either source.
  // --------------------------------------------------------------------------

  const originalNormalizeContext = E.normalizeContext.bind(E);

  E.normalizeContext = function normalizeContextV31(context = {}) {
    const ctx = originalNormalizeContext(context);
    const K = this.knowledge();

    const narrative =
      context.parentNotes ||
      context.narrative ||
      context.description ||
      context.answers?.parentNotes ||
      context.answers?.narrative ||
      context.answers?.description ||
      "";

    const narrativeMatches = K.findV3ObservationsInNarrative
      ? K.findV3ObservationsInNarrative(narrative)
      : [];

    const narrativeObservations = narrativeMatches.map((item) => item.observation);

    ctx.parentNotes = narrative;
    ctx.narrativeMatches = narrativeMatches;
    ctx.observations = this.unique(
      this.normalizeObservations(ctx.observations).concat(narrativeObservations)
    );

    return ctx;
  };

  // --------------------------------------------------------------------------
  // 2. ADAPTIVE ANSWER SCORING
  // Uses exact option scoring from the revised knowledge library.
  // --------------------------------------------------------------------------

  E.applyAdaptiveAnswerScores = function applyAdaptiveAnswerScoresV31(scores, adaptiveAnswers = {}) {
    const K = this.knowledge();
    const questions = K.v3?.adaptiveQuestions || [];

    questions.forEach((question) => {
      const raw = adaptiveAnswers[question.id];
      if (raw === undefined || raw === null || raw === "") return;

      let optionIndex = -1;

      if (typeof raw === "number") {
        optionIndex = raw;
      } else if (typeof raw === "object" && raw !== null) {
        if (Number.isInteger(raw.optionIndex)) optionIndex = raw.optionIndex;
        else if (Number.isInteger(raw.index)) optionIndex = raw.index;
        else {
          const candidate = raw.value ?? raw.answer ?? raw.label;
          optionIndex = (question.options || []).findIndex(
            (option) => this.normalizeKey(option.label) === this.normalizeKey(candidate)
          );
        }
      } else {
        optionIndex = (question.options || []).findIndex(
          (option) => this.normalizeKey(option.label) === this.normalizeKey(raw)
        );
      }

      if (optionIndex < 0 || !question.options?.[optionIndex]) return;

      const scoreMap = question.options[optionIndex].scores || {};
      Object.keys(scoreMap).forEach((constructId) => {
        if (!scores[constructId]) return;
        scores[constructId].score = Math.max(
          0,
          scores[constructId].score + Number(scoreMap[constructId] || 0)
        );
        scores[constructId].adaptiveEvidence.push(
          `${question.question} → ${question.options[optionIndex].label}`
        );
      });
    });
  };

  // --------------------------------------------------------------------------
  // 3. AGE-AWARE HELPERS
  // --------------------------------------------------------------------------

  E.getAgeBand = function getAgeBand(ctx) {
    const K = this.knowledge();
    const age = ctx.child?.age ?? ctx.raw?.answers?.age ?? ctx.raw?.age;
    return K.getV3AgeBand ? K.getV3AgeBand(age) : null;
  };

  E.ageLanguage = function ageLanguage(ctx, guidance = []) {
    const band = this.getAgeBand(ctx);
    if (!band) return guidance;

    const agePrefix = {
      Toddler: "Keep the step concrete, physical and very short.",
      "Early Childhood": "Use simple choices, pictures and repetition.",
      Elementary: "Invite limited problem solving and responsibility.",
      "Young Teen": "Protect dignity and collaborate before enforcing.",
      "Older Teen": "Use consultation, ownership and natural consequences."
    }[band.label];

    return this.unique([agePrefix].concat(guidance || [])).filter(Boolean);
  };

  // --------------------------------------------------------------------------
  // 4. SCENARIO-SPECIFIC GUIDANCE
  // Uses Scenario + Construct guidance from the new knowledge library.
  // --------------------------------------------------------------------------

  E.getScenarioGuidance = function getScenarioGuidance(reasoning, constructId) {
    const K = this.knowledge();
    const scenarioKey =
      reasoning.scenario?.situationKey ||
      reasoning.situationKey ||
      reasoning.scenario?.key ||
      "";

    return K.getV3ScenarioConstructGuidance
      ? K.getV3ScenarioConstructGuidance(scenarioKey, constructId)
      : null;
  };

  // --------------------------------------------------------------------------
  // 5. HUMAN WORKING UNDERSTANDING
  // --------------------------------------------------------------------------

  E.generateWorkingUnderstanding = function generateWorkingUnderstandingV31(ctx, scenario, topConstructs = []) {
    const childName = ctx.child?.name || ctx.child?.childName || "your child";
    const ageBand = this.getAgeBand(ctx);
    const top = topConstructs[0];
    const second = topConstructs[1];
    const third = topConstructs[2];

    if (!top) {
      return {
        headline: "We need a little more information.",
        summary: `We do not yet have enough evidence to explain what is driving this for ${childName}.`,
        bullets: [
          "Choose the behaviors you are actually seeing.",
          "Answer two or three follow-up questions.",
          "The explanation should become more specific as the pattern becomes clearer."
        ],
        ageBand: ageBand?.label || null
      };
    }

    const scenarioLabel = scenario?.label || this.labelSituation(scenario?.situationKey);
    const primaryGuidance = this.getScenarioGuidance(
      { scenario, situationKey: scenario?.situationKey },
      top.id
    );

    const opening = primaryGuidance?.insight
      ? primaryGuidance.insight
      : `${top.label} appears to be the strongest current pattern.`;

    const secondarySentence = second
      ? `We are also seeing signs of ${second.label.toLowerCase()}.`
      : "";

    const nuanceSentence = third
      ? `${third.label} may be a smaller contributing factor rather than the main cause.`
      : "";

    const ageSentence = ageBand
      ? `Because ${childName} is in the ${ageBand.label.toLowerCase()} stage, the plan should match that developmental level.`
      : "";

    return {
      headline: `What we think may be happening with ${scenarioLabel.toLowerCase()}`,
      summary: [
        opening,
        secondarySentence,
        nuanceSentence,
        ageSentence,
        "This is a working explanation to test, not a diagnosis."
      ].filter(Boolean).join(" "),
      bullets: topConstructs.slice(0, 3).map((construct) => {
        const evidence = []
          .concat(construct.evidence || [])
          .concat(construct.adaptiveEvidence || [])
          .slice(0, 2);

        return {
          constructId: construct.id,
          label: construct.label,
          confidence: construct.confidence?.label || construct.confidence || "low",
          evidence
        };
      }),
      ageBand: ageBand?.label || null
    };
  };

  // --------------------------------------------------------------------------
  // 6. PLAN BUILDER
  // Replaces generic construct-only advice with scenario-specific advice first,
  // then falls back to construct-level guidance.
  // --------------------------------------------------------------------------

  E.buildSteps = function buildStepsV31(ctx, reasoning, interventions) {
    const top = reasoning.topConstructs[0];
    const second = reasoning.topConstructs[1];
    const topGuidance = top ? this.getScenarioGuidance(reasoning, top.id) : null;
    const secondGuidance = second ? this.getScenarioGuidance(reasoning, second.id) : null;

    let steps = [];
    if (topGuidance?.steps?.length) steps = steps.concat(topGuidance.steps);
    if (secondGuidance?.steps?.length) steps = steps.concat(secondGuidance.steps.slice(0, 1));

    if (!steps.length) {
      const helps = this.flatten(
        (interventions || []).map((item) => item.intervention?.usuallyHelps || [])
      );

      steps = helps.slice(0, 3).map(
        (help) => `Use ${String(help).toLowerCase()} as the next small strategy.`
      );
    }

    if (!steps.length) {
      steps = [
        "Name the next step calmly.",
        "Make the choice simple and concrete.",
        "Follow through without adding a long lecture."
      ];
    }

    const ageAdjusted = this.ageLanguage(ctx, steps);
    return this.unique(ageAdjusted).slice(0, 3);
  };

  E.buildAvoidList = function buildAvoidListV31(reasoning, interventions) {
    const top = reasoning.topConstructs[0];
    const second = reasoning.topConstructs[1];

    const scenarioAvoid = []
      .concat(top ? this.getScenarioGuidance(reasoning, top.id)?.avoid || [] : [])
      .concat(second ? this.getScenarioGuidance(reasoning, second.id)?.avoid || [] : []);

    const constructAvoid = this.flatten(
      (interventions || []).map((item) => item.intervention?.usuallyBackfires || [])
    );

    const extra = [];
    if (this.requiresCalmingFirst(reasoning)) {
      extra.push("Do not lecture while the child is emotionally flooded.");
    }

    return this.unique(scenarioAvoid.concat(extra, constructAvoid)).slice(0, 3);
  };

  E.buildScripts = function buildScriptsV31(ctx, reasoning) {
    const K = this.knowledge();
    const top = reasoning.topConstructs[0];
    const scenario = reasoning.scenario;
    const situation = K.getSituation
      ? K.getSituation(scenario?.situationKey || reasoning.situationKey)
      : null;

    const baseScript =
      situation?.parentScript?.soft ||
      "I understand this is hard. We are still going to take the next small step.";

    const topScript = {
      C01: "You can choose this way or that way. The boundary stays the same.",
      C02: "A change is coming. I will help you with the first step.",
      C03: "One step first. When that is done, we will look at the next one.",
      C04: "I need your attention for one moment. Here is the one thing to do.",
      C05: "I see that this feels big. I am going to stay calm with you.",
      C06: "We are pausing until your body is calmer. Then we will solve it.",
      C07: "The worry can be here, and we can still take one small brave step.",
      C08: "You do not have to do it perfectly. Start with one manageable part.",
      C09: "You can handle the first step. I am looking for effort, not perfection.",
      C10: "I hear that it feels unfair. I will explain once, and the limit remains.",
      C11: "I know you want to keep going. The stopping point is still here.",
      C12: "Let us first check what feels uncomfortable in your body or environment.",
      C13: "I am with you. First we connect, then we take the next step.",
      C14: "I have answered. Continuing to ask will not change the answer.",
      C15: "The adults are using the same rule and the same answer."
    }[top?.id];

    return {
      main: topScript || baseScript,
      scenario: baseScript,
      repair:
        situation?.parentScript?.repair ||
        "That was a hard moment. We are okay. Next time we will practice one better step."
    };
  };

  E.buildWhy = function buildWhyV31(ctx, reasoning) {
    const childName = ctx.child?.name || ctx.child?.childName || "your child";
    const top = reasoning.topConstructs[0];
    const second = reasoning.topConstructs[1];

    if (!top) {
      return "The plan stays simple because we need more information before assuming what is driving the behavior.";
    }

    const topGuidance = this.getScenarioGuidance(reasoning, top.id);
    const first = topGuidance?.insight || `${top.label} appears to be the main current pattern.`;
    const secondSentence = second
      ? `${second.label} may be influencing how strongly the situation develops.`
      : "";

    return `${childName} may not simply be choosing to be difficult. ${first} ${secondSentence}`.trim();
  };

  E.buildWhatToNotice = function buildWhatToNoticeV31(reasoning) {
    const top = reasoning.topConstructs[0];
    const second = reasoning.topConstructs[1];
    const questions = [];

    const byConstruct = {
      C01: "Does cooperation improve when the child has one meaningful choice?",
      C02: "Do warnings and transition rituals reduce resistance?",
      C03: "Does one-step structure reduce reminders?",
      C04: "Does getting close before speaking improve follow-through?",
      C05: "Does a softer, private correction reduce the reaction?",
      C06: "How long does recovery take when adults use fewer words?",
      C07: "Does reassurance settle the child or restart the question loop?",
      C08: "Does a smaller first step make starting easier?",
      C09: "Does specific encouragement increase willingness to try?",
      C10: "Does one clear explanation reduce fairness arguments?",
      C11: "Does a closure ritual help the child let go?",
      C12: "Which sensory conditions make the situation better or worse?",
      C13: "Does connection before correction reduce resistance?",
      C14: "Does consistent follow-through reduce future negotiation?",
      C15: "Does using the same adult response reduce testing?"
    };

    if (top && byConstruct[top.id]) questions.push(byConstruct[top.id]);
    if (second && byConstruct[second.id]) questions.push(byConstruct[second.id]);

    if (!questions.length) {
      questions.push(
        "What helped?",
        "What made the situation worse?",
        "What should we test next?"
      );
    }

    return this.unique(questions).slice(0, 3);
  };

  // --------------------------------------------------------------------------
  // 7. PLAN TITLE/SUBTITLE
  // Makes result headings scenario-aware and less repetitive.
  // --------------------------------------------------------------------------

  const originalBuildPlan = E.buildPlan.bind(E);

  E.buildPlan = function buildPlanV31(ctx, reasoning) {
    const plan = originalBuildPlan(ctx, reasoning);
    const top = reasoning.topConstructs[0];
    const second = reasoning.topConstructs[1];
    const label = reasoning.scenario?.label || this.labelSituation(reasoning.situationKey);

    plan.title = top
      ? `${label}: a plan built around ${top.label.toLowerCase()}`
      : `${label}: a starting plan`;

    plan.subtitle = second
      ? `The plan also accounts for ${second.label.toLowerCase()} and ${this.getAgeBand(ctx)?.label?.toLowerCase() || "developmental"} needs.`
      : `A practical next step based on what we know so far.`;

    return plan;
  };

  // --------------------------------------------------------------------------
  // 8. REASONING DEBUG DETAIL
  // Adds narrative evidence and age band to the result.
  // --------------------------------------------------------------------------

  const originalGeneratePlan = E.generatePlan.bind(E);

  E.generatePlan = function generatePlanV31(context = {}) {
    const result = originalGeneratePlan(context);
    const ctx = this.normalizeContext(context);

    result.version = this.version;
    result.ageBand = this.getAgeBand(ctx);
    result.debug = {
      ...(result.debug || {}),
      narrativeMatches: ctx.narrativeMatches || [],
      normalizedObservations: ctx.observations || [],
      ageBand: result.ageBand
    };

    return result;
  };

})(window);


/* ==========================================================================
 * Parenting Optimize Engine — V3.3 Paid Blueprint Runtime
 * Appended to the stable V3 engine. This layer adds the full paid Blueprint
 * while preserving the existing scoring, memory, and public APIs.
 * ========================================================================== */
(function (window) {
  "use strict";

  const PO = (window.PO = window.PO || {});
  const E = PO.Engine;

  if (!E) {
    throw new Error("Parenting Optimize V3 engine must load before V3.3.");
  }

  E.version = "3.3.0";

  // --------------------------------------------------------------------------
  // MAIN V3.3 API
  // --------------------------------------------------------------------------

  const originalGeneratePlanV33 = E.generatePlan.bind(E);

  E.generatePlan = function generatePlanV33(context = {}) {
    const result = originalGeneratePlanV33(context);
    const ctx = this.normalizeContext(context);
    const reasoning = this.reason(ctx);
    const blueprint = this.buildPaidBlueprint(ctx, reasoning, result);

    return {
      ...result,
      version: this.version,
      freePreview: blueprint.freePreview,
      paidBlueprint: blueprint.paidBlueprint,
      confidenceDetail: blueprint.confidenceDetail,
      howWeReachedThis: blueprint.paidBlueprint.howWeReachedThis,
      childProfile: blueprint.paidBlueprint.childProfile,
      parentChildLoop: blueprint.paidBlueprint.parentChildLoop,
      detailedPlan: blueprint.paidBlueprint.beforeDuringAfterPlan,
      exampleConversation: blueprint.paidBlueprint.exampleConversation,
      motivationAndRewards: blueprint.paidBlueprint.motivationAndRewards,
      coParenting: blueprint.paidBlueprint.coParenting,
      sevenDayPlan: blueprint.paidBlueprint.sevenDayPlan,
      progressMarkers: blueprint.paidBlueprint.progressMarkers,
      alternateExplanation: blueprint.paidBlueprint.alternateExplanation,
      ifPlanFails: blueprint.paidBlueprint.ifPlanFails,
      professionalSupport: blueprint.paidBlueprint.professionalSupport,
      debug: {
        ...(result.debug || {}),
        blueprintConstructIds: reasoning.topConstructs.slice(0, 3).map((item) => item.id),
        blueprintScenario: reasoning.scenario?.id || reasoning.scenario?.key || reasoning.situationKey
      }
    };
  };

  // --------------------------------------------------------------------------
  // BLUEPRINT ASSEMBLER
  // --------------------------------------------------------------------------

  E.buildPaidBlueprint = function buildPaidBlueprint(ctx, reasoning, basePlan = {}) {
    const K = this.knowledge();
    const childName = ctx.child?.name || ctx.child?.childName || "your child";
    const childAge = Number(ctx.child?.age || 0) || null;
    const scenario = reasoning.scenario || {};
    const scenarioId = scenario.id || ctx.scenarioId || scenario.key || reasoning.situationKey;
    const scenarioLabel = scenario.label || basePlan.situationLabel || this.labelSituation(reasoning.situationKey);
    const top = reasoning.topConstructs[0] || null;
    const second = reasoning.topConstructs[1] || null;
    const third = reasoning.topConstructs[2] || null;

    const primaryBlueprint = top && K.getV3ConstructBlueprint
      ? K.getV3ConstructBlueprint(top.id)
      : null;

    const secondaryBlueprint = second && K.getV3ConstructBlueprint
      ? K.getV3ConstructBlueprint(second.id)
      : null;

    const thirdBlueprint = third && K.getV3ConstructBlueprint
      ? K.getV3ConstructBlueprint(third.id)
      : null;

    const scenarioBlueprint = K.getV3ScenarioBlueprint
      ? K.getV3ScenarioBlueprint(scenarioId)
      : null;

    const confidenceDetail = this.buildConfidenceDetail(reasoning);
    const evidence = this.buildEvidenceExplanation(ctx, reasoning, scenarioLabel);
    const childProfile = this.buildChildProfileSection(
      childName,
      childAge,
      top,
      second,
      primaryBlueprint,
      secondaryBlueprint
    );

    const situationAnalysis = this.buildSituationAnalysis(
      childName,
      scenarioLabel,
      scenarioBlueprint,
      top,
      second,
      primaryBlueprint,
      secondaryBlueprint
    );

    const parentChildLoop = this.buildParentChildLoop(
      ctx,
      childName,
      scenarioBlueprint,
      top,
      primaryBlueprint
    );

    const beforeDuringAfterPlan = this.buildBeforeDuringAfterPlan(
      ctx,
      scenarioBlueprint,
      primaryBlueprint,
      secondaryBlueprint,
      basePlan
    );

    const scripts = this.buildBlueprintScripts(
      childName,
      scenarioBlueprint,
      primaryBlueprint,
      secondaryBlueprint,
      basePlan
    );

    const exampleConversation = this.buildExampleConversation(
      childName,
      scenarioBlueprint,
      primaryBlueprint,
      scripts
    );

    const motivationAndRewards = this.buildMotivationAndRewards(
      primaryBlueprint,
      secondaryBlueprint
    );

    const coParenting = this.buildCoParentingSection(
      ctx,
      primaryBlueprint,
      secondaryBlueprint
    );

    const sevenDayPlan = K.buildV3SevenDayPlan
      ? K.buildV3SevenDayPlan(scenarioId, [top?.id, second?.id].filter(Boolean))
      : this.fallbackSevenDayPlan(beforeDuringAfterPlan);

    const progressMarkers = this.buildProgressMarkers(
      primaryBlueprint,
      secondaryBlueprint,
      basePlan
    );

    const alternateExplanation = this.buildAlternateExplanation(
      second,
      third,
      secondaryBlueprint,
      thirdBlueprint
    );

    const ifPlanFails = this.buildIfPlanFails(
      primaryBlueprint,
      secondaryBlueprint,
      scenarioBlueprint
    );

    const professionalSupport = this.buildProfessionalSupportSection(
      reasoning,
      scenarioLabel
    );

    const freePreview = {
      productName: K.v3?.paidBlueprint?.productName || "Personalized Parenting Blueprint",
      patternSummary:
        reasoning.workingUnderstanding?.summary ||
        basePlan.why ||
        `${top?.label || "A pattern"} may be contributing to this situation.`,
      topDrivers: reasoning.topConstructs.slice(0, 2).map((item) => ({
        id: item.id,
        label: item.label,
        meaning: item.meaning,
        confidence: this.confidenceLabel(item.confidence)
      })),
      confidencePreview: confidenceDetail,
      oneImmediateInsight:
        situationAnalysis.keyInsight ||
        basePlan.steps?.[0] ||
        "Use one small, repeatable change and observe what shifts."
    };

    return {
      freePreview,
      confidenceDetail,
      paidBlueprint: {
        version: "3.3.0",
        productName: K.v3?.paidBlueprint?.productName || "Personalized Parenting Blueprint",
        childName,
        childAge,
        scenarioId,
        scenarioLabel,
        generatedAt: this.now(),
        childProfile,
        howWeReachedThis: evidence,
        situationAnalysis,
        parentChildLoop,
        beforeDuringAfterPlan,
        scripts,
        exampleConversation,
        motivationAndRewards,
        coParenting,
        sevenDayPlan,
        progressMarkers,
        alternateExplanation,
        ifPlanFails,
        professionalSupport,
        disclaimer:
          K.v3?.paidBlueprint?.disclaimer ||
          "Educational guidance only. This is not a diagnosis, treatment plan, or substitute for professional care."
      }
    };
  };

  // --------------------------------------------------------------------------
  // CONFIDENCE
  // --------------------------------------------------------------------------

  E.buildConfidenceDetail = function buildConfidenceDetail(reasoning) {
    const top = reasoning.topConstructs[0];
    const score = Number(top?.score || 0);
    const evidenceCount = this.unique(
      []
        .concat(top?.evidence || [])
        .concat(top?.adaptiveEvidence || [])
        .concat(top?.memoryEvidence || [])
    ).length;

    const rawPercent = Math.round(
      Math.min(
        94,
        Math.max(
          42,
          42 + score * 6 + evidenceCount * 3
        )
      )
    );

    const level =
      reasoning.overallConfidence === "high" || rawPercent >= 78
        ? "high"
        : reasoning.overallConfidence === "medium" || rawPercent >= 60
          ? "medium"
          : "low";

    const K = this.knowledge();
    const language =
      K.v3?.paidBlueprint?.confidenceLanguage?.[level] ||
      (level === "high"
        ? "Multiple answers point toward the same working explanation."
        : level === "medium"
          ? "Several answers support this explanation, although another factor may also matter."
          : "This is an early possibility and should be tested carefully.");

    return {
      level,
      percent: rawPercent,
      evidenceCount,
      language
    };
  };

  E.confidenceLabel = function confidenceLabel(value) {
    if (!value) return "Low";
    if (typeof value === "string") return this.titleCase(value);
    return value.label || this.titleCase(value.language || "Low");
  };

  // --------------------------------------------------------------------------
  // CHILD PROFILE
  // --------------------------------------------------------------------------

  E.buildChildProfileSection = function buildChildProfileSection(
    childName,
    childAge,
    top,
    second,
    primary,
    secondary
  ) {
    const strengths = this.unique(
      []
        .concat(primary?.strengths || [])
        .concat(secondary?.strengths || [])
    ).slice(0, 5);

    const triggers = this.unique(
      []
        .concat(primary?.stressTriggers || [])
        .concat(secondary?.stressTriggers || [])
    ).slice(0, 5);

    const motivation = this.unique(
      []
        .concat(primary?.motivation || [])
        .concat(secondary?.motivation || [])
    ).slice(0, 5);

    const profileText = [
      primary?.profile ||
        `${childName} appears to show signs of ${top?.label?.toLowerCase() || "a recurring behavioral pattern"}.`,
      secondary?.profile
        ? `A second layer may be ${second?.label?.toLowerCase()}: ${secondary.profile}`
        : ""
    ].filter(Boolean).join(" ");

    return {
      headline: `A working profile of ${childName}`,
      age: childAge,
      summary: profileText,
      primaryPattern: top
        ? {
            id: top.id,
            label: top.label,
            meaning: top.meaning,
            confidence: this.confidenceLabel(top.confidence)
          }
        : null,
      secondaryPattern: second
        ? {
            id: second.id,
            label: second.label,
            meaning: second.meaning,
            confidence: this.confidenceLabel(second.confidence)
          }
        : null,
      strengths,
      stressTriggers: triggers,
      respondsBestTo: motivation,
      note:
        "This profile describes the pattern visible in this situation. It should remain flexible as more situations and feedback are added."
    };
  };

  // --------------------------------------------------------------------------
  // EVIDENCE / HOW WE REACHED THIS
  // --------------------------------------------------------------------------

  E.buildEvidenceExplanation = function buildEvidenceExplanation(ctx, reasoning, scenarioLabel) {
    const observations = this.unique(reasoning.observations || ctx.observations || []);
    const top = reasoning.topConstructs[0];
    const second = reasoning.topConstructs[1];

    const topEvidence = this.unique(
      []
        .concat(top?.evidence || [])
        .concat(top?.adaptiveEvidence || [])
    ).slice(0, 5);

    const secondEvidence = this.unique(
      []
        .concat(second?.evidence || [])
        .concat(second?.adaptiveEvidence || [])
    ).slice(0, 3);

    const narrative = String(ctx.parentNotes || ctx.raw?.description || ctx.raw?.narrative || "").trim();

    const conclusion = top
      ? `Together, these details make ${top.label.toLowerCase()} the strongest current explanation for ${scenarioLabel.toLowerCase()}.`
      : "We need more information before naming a strong working explanation.";

    return {
      headline: "How we reached this conclusion",
      narrativeExcerpt: narrative ? narrative.slice(0, 280) : "",
      observations: observations.slice(0, 8),
      primaryEvidence: topEvidence,
      secondaryEvidence: secondEvidence,
      explanation: [
        observations.length
          ? `We identified ${observations.length} relevant behavior signal${observations.length === 1 ? "" : "s"}.`
          : "",
        top && second
          ? `${top.label} appeared more consistently than ${second.label}.`
          : "",
        conclusion
      ].filter(Boolean).join(" ")
    };
  };

  // --------------------------------------------------------------------------
  // SITUATION ANALYSIS
  // --------------------------------------------------------------------------

  E.buildSituationAnalysis = function buildSituationAnalysis(
    childName,
    scenarioLabel,
    scenario,
    top,
    second,
    primary,
    secondary
  ) {
    const keyInsight =
      scenario?.commonLoop?.length
        ? `${scenarioLabel} may be repeating because the situation follows a predictable loop rather than because ${childName} simply chooses not to cooperate.`
        : primary?.profile ||
          `${top?.label || "The primary pattern"} may be shaping this situation.`;

    return {
      headline: `Why ${scenarioLabel.toLowerCase()} may keep repeating`,
      keyInsight,
      primaryDriver: {
        label: top?.label || "",
        explanation:
          primary?.profile ||
          top?.meaning ||
          "This appears to be the strongest current driver."
      },
      secondaryDriver: second
        ? {
            label: second.label,
            explanation:
              secondary?.profile ||
              second.meaning ||
              "This may intensify the primary pattern."
          }
        : null,
      commonLoop: scenario?.commonLoop || [],
      whatThisMayNotBe:
        top?.id === "C03" || top?.id === "C04"
          ? "This may not be deliberate laziness or ignoring."
          : top?.id === "C07" || top?.id === "C13"
            ? "This may not be manipulation; reassurance or avoidance may be serving a coping function."
            : top?.id === "C06"
              ? "This may not be a useful teaching moment while the child is emotionally flooded."
              : "This should not automatically be treated as simple defiance."
    };
  };

  // --------------------------------------------------------------------------
  // PARENT-CHILD LOOP
  // --------------------------------------------------------------------------

  E.buildParentChildLoop = function buildParentChildLoop(
    ctx,
    childName,
    scenario,
    top,
    primary
  ) {
    const parentStyle =
      ctx.parent?.responseStyle ||
      ctx.raw?.answers?.parentResponse ||
      "unspecified";

    const parentResponse = this.knowledge().getParentResponse
      ? this.knowledge().getParentResponse(parentStyle)
      : null;

    const loop = scenario?.commonLoop?.length
      ? scenario.commonLoop
      : [
          "A demand or trigger appears",
          `${childName} reacts through ${top?.label?.toLowerCase() || "the current pattern"}`,
          "The adult increases words, pressure, reminders, or help",
          "The child's resistance or emotion increases",
          "The interaction becomes more important than the original task"
        ];

    return {
      headline: "The loop between behavior and response",
      steps: loop,
      parentPattern: parentResponse?.label || "Natural parent stress response",
      parentStrengths: parentResponse?.strengths || [],
      underStress: parentResponse?.underStress || [],
      adjustment:
        primary?.coParenting ||
        parentResponse?.scriptGuidance?.opening ||
        "Use fewer words, hold one clear boundary, and repair after calm returns."
    };
  };

  // --------------------------------------------------------------------------
  // DETAILED PLAN
  // --------------------------------------------------------------------------

  E.buildBeforeDuringAfterPlan = function buildBeforeDuringAfterPlan(
    ctx,
    scenario,
    primary,
    secondary,
    basePlan
  ) {
    const before = this.unique(
      []
        .concat(scenario?.before || [])
        .concat(primary?.motivation?.slice(0, 1) || [])
    ).slice(0, 4);

    const during = this.unique(
      []
        .concat(scenario?.during || [])
        .concat(basePlan.steps || [])
    ).slice(0, 5);

    const after = this.unique(
      []
        .concat(scenario?.after || [])
        .concat(primary?.progress?.slice(0, 1) || [])
    ).slice(0, 4);

    const age = Number(ctx.child?.age || 0);
    const ageNote =
      age && age <= 7
        ? "Keep words brief, visible, and concrete. Practice through repetition rather than long discussion."
        : age && age <= 11
          ? "Use brief reasons, visible steps, and limited participation in problem solving."
          : age
            ? "Protect dignity, involve the child before conflict, and connect freedom with responsibility."
            : "";

    return {
      headline: "Your personalized action plan",
      ageNote,
      before: before.length ? before : ["Prepare one clear routine before the difficult moment."],
      during: during.length ? during : ["Use one calm sentence and one clear next step."],
      after: after.length ? after : ["Reconnect briefly and record what helped."],
      firstMoveTonight:
        during[0] ||
        basePlan.steps?.[0] ||
        "Use one small, repeatable change tonight."
    };
  };

  // --------------------------------------------------------------------------
  // SCRIPTS + EXAMPLE
  // --------------------------------------------------------------------------

  E.buildBlueprintScripts = function buildBlueprintScripts(
    childName,
    scenario,
    primary,
    secondary,
    basePlan
  ) {
    return {
      warm:
        primary?.warmScript ||
        basePlan.scripts?.main ||
        `I understand this is hard, ${childName}. We are still taking the next step.`,
      firm:
        primary?.firmScript ||
        scenario?.examples?.[0]?.try ||
        "The boundary stays the same. I will help you begin.",
      repair:
        primary?.repairScript ||
        basePlan.scripts?.repair ||
        "That was a hard moment. We are okay, and we will practice a better next step.",
      secondary:
        secondary?.warmScript ||
        "I see another part of this may be difficult too. We will keep the plan simple.",
      insteadOf:
        primary?.insteadOf ||
        scenario?.examples?.[0]?.instead ||
        "Stop arguing and do what I said.",
      tryInstead:
        primary?.tryInstead ||
        scenario?.examples?.[0]?.try ||
        "I will explain once, then we will take the next step."
    };
  };

  E.buildExampleConversation = function buildExampleConversation(
    childName,
    scenario,
    primary,
    scripts
  ) {
    const scenarioExample = scenario?.examples?.[0];

    return {
      headline: "What this could sound like in real life",
      setup:
        `The difficult moment begins, and ${childName} starts showing the usual resistance or emotion.`,
      parentOpening: scripts.warm,
      likelyChildResponse:
        primary?.name === "Need for Control"
          ? "But I do not want either choice. I want to do it my way."
          : primary?.name === "Anxiety / Worry"
            ? "But what if something goes wrong?"
            : primary?.name === "Transition Difficulty"
              ? "I am not finished yet."
              : "I do not want to.",
      parentBoundary: scripts.firm,
      repair: scripts.repair,
      insteadOf: scenarioExample?.instead || scripts.insteadOf,
      tryInstead: scenarioExample?.try || scripts.tryInstead
    };
  };

  // --------------------------------------------------------------------------
  // MOTIVATION + REWARDS
  // --------------------------------------------------------------------------

  E.buildMotivationAndRewards = function buildMotivationAndRewards(primary, secondary) {
    return {
      headline: "Motivation and rewards that fit this pattern",
      likelyMotivators: this.unique(
        []
          .concat(primary?.motivation || [])
          .concat(secondary?.motivation || [])
      ).slice(0, 6),
      rewardIdeas: this.unique(
        []
          .concat(primary?.rewardIdeas || [])
          .concat(secondary?.rewardIdeas || [])
      ).slice(0, 6),
      avoid: this.unique(
        []
          .concat(primary?.rewardBackfires || [])
          .concat(secondary?.rewardBackfires || [])
      ).slice(0, 4),
      principle:
        "Use rewards to strengthen a skill or successful routine—not to purchase cooperation after escalation has already begun."
    };
  };

  // --------------------------------------------------------------------------
  // CO-PARENTING
  // --------------------------------------------------------------------------

  E.buildCoParentingSection = function buildCoParentingSection(ctx, primary, secondary) {
    const structure =
      ctx.family?.structure ||
      ctx.raw?.answers?.familyStructure ||
      "two-together";

    const family = this.knowledge().getFamilyStructure
      ? this.knowledge().getFamilyStructure(structure)
      : null;

    return {
      headline: "How adults can stay aligned",
      familyContext: family?.language || "The plan works best when adults use the same basic structure.",
      primaryGuidance:
        primary?.coParenting ||
        "Agree privately on the limit and use the same wording.",
      secondaryGuidance:
        secondary?.coParenting ||
        "Avoid correcting another adult in front of the child.",
      sharedScript:
        "We have agreed on the same plan. You may speak with either of us, and the boundary will remain the same.",
      avoid: [
        "One adult reversing the other in front of the child",
        "Good-cop/bad-cop roles",
        "Debating the plan while the child is waiting"
      ]
    };
  };

  // --------------------------------------------------------------------------
  // SEVEN DAY PLAN
  // --------------------------------------------------------------------------

  E.fallbackSevenDayPlan = function fallbackSevenDayPlan(plan) {
    return [
      {
        day: "Days 1–2",
        title: "Observe and prepare",
        actions: [
          plan.before?.[0] || "Notice the earliest trigger.",
          "Write down what happens immediately before the behavior.",
          "Choose one response the adults can repeat."
        ]
      },
      {
        day: "Days 3–4",
        title: "Test the main strategy",
        actions: [
          plan.during?.[0] || "Use one calm response.",
          "Repeat it consistently in two similar moments.",
          "Track conflict intensity and recovery time."
        ]
      },
      {
        day: "Days 5–7",
        title: "Adjust and reinforce",
        actions: [
          plan.after?.[0] || "Repair after calm returns.",
          "Praise the specific skill you want repeated.",
          "Decide whether to continue or test the alternate explanation."
        ]
      }
    ];
  };

  // --------------------------------------------------------------------------
  // PROGRESS + ALTERNATES + FAILURE PATH
  // --------------------------------------------------------------------------

  E.buildProgressMarkers = function buildProgressMarkers(primary, secondary, basePlan) {
    return {
      headline: "What improvement should look like",
      earlySigns: this.unique(
        []
          .concat(primary?.progress || [])
          .concat(secondary?.progress || [])
      ).slice(0, 6),
      whatToTrack: basePlan.whatToNotice || [
        "How quickly the child begins",
        "How intense the conflict becomes",
        "How long recovery takes"
      ],
      successRule:
        "Success does not require perfect cooperation. Look first for shorter conflict, faster recovery, fewer reminders, or better use of words."
    };
  };

  E.buildAlternateExplanation = function buildAlternateExplanation(
    second,
    third,
    secondary,
    tertiary
  ) {
    const alternate = second || third;
    const blueprint = second ? secondary : tertiary;

    if (!alternate) {
      return {
        headline: "Another explanation to keep in mind",
        explanation:
          "There is not enough evidence for a strong alternate explanation yet.",
        whenToReconsider: [
          "The plan repeatedly makes the situation worse",
          "The same behavior appears for very different reasons in other settings"
        ]
      };
    }

    return {
      headline: "Another explanation to keep in mind",
      label: alternate.label,
      explanation:
        blueprint?.profile ||
        alternate.meaning ||
        "This may be contributing even if it is not the main driver.",
      whenToReconsider:
        blueprint?.ifFails?.slice(0, 3) || [
          "The primary strategy does not improve the pattern",
          "New observations point more strongly in this direction"
        ]
    };
  };

  E.buildIfPlanFails = function buildIfPlanFails(primary, secondary, scenario) {
    return {
      headline: "If the first plan does not work",
      steps: this.unique(
        []
          .concat(primary?.ifFails || [])
          .concat(secondary?.ifFails || [])
          .concat([
            "Check whether the child was already emotionally flooded before the strategy began.",
            "Change only one variable at a time.",
            "Use the next session to test the alternate explanation."
          ])
      ).slice(0, 6),
      scenarioFallback:
        scenario?.after?.[1] ||
        "Return to observation and identify the earliest point where the loop begins."
    };
  };

  // --------------------------------------------------------------------------
  // PROFESSIONAL SUPPORT
  // --------------------------------------------------------------------------

  E.buildProfessionalSupportSection = function buildProfessionalSupportSection(
    reasoning,
    scenarioLabel
  ) {
    const flags = reasoning.referralFlags || [];

    return {
      headline: "When additional support may be useful",
      currentFlags: flags,
      generalIndicators: [
        "The behavior creates an immediate safety risk",
        "There is repeated aggression, running away, self-harm language, or threats",
        "The child is losing skills or showing severe ongoing distress",
        "School, sleep, eating, or ordinary family life is significantly disrupted",
        "The pattern remains severe despite consistent support"
      ],
      message:
        flags.length
          ? `Some of the information connected with ${scenarioLabel.toLowerCase()} may deserve discussion with a qualified pediatric, educational, or mental-health professional.`
          : "Nothing in the current pattern automatically means professional care is required, but persistent or severe impairment should not be managed only through an app."
    };
  };

  // --------------------------------------------------------------------------
  // MEMORY PATCH: SAVE BLUEPRINT SIGNALS
  // --------------------------------------------------------------------------

  const originalBuildChildProfilePatchV33 = E.buildChildProfilePatch.bind(E);

  E.buildChildProfilePatch = function buildChildProfilePatchV33(ctx, reasoning, plan) {
    const patch = originalBuildChildProfilePatchV33(ctx, reasoning, plan);
    const blueprint = this.buildPaidBlueprint(ctx, reasoning, {
      ...plan,
      situationLabel:
        reasoning.scenario?.label || this.labelSituation(reasoning.situationKey)
    });

    return {
      ...patch,
      lastBlueprintSummary: {
        scenario:
          reasoning.scenario?.id || reasoning.scenario?.key || reasoning.situationKey,
        primaryConstruct: reasoning.topConstructs[0]?.id || null,
        secondaryConstruct: reasoning.topConstructs[1]?.id || null,
        confidence: blueprint.confidenceDetail,
        firstMove:
          blueprint.paidBlueprint.beforeDuringAfterPlan?.firstMoveTonight || "",
        generatedAt: this.now()
      }
    };
  };

  // --------------------------------------------------------------------------
  // UTILITIES
  // --------------------------------------------------------------------------

  E.titleCase = function titleCase(value) {
    return String(value || "")
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

})(window);
